import React, { useState, useEffect, useRef } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import type { GraphNode } from '../../api/dastarkhan';
import { usePermissions } from '../../hooks/usePermissions';
import { PropertyEditor } from './PropertyEditor';
import {
  Card,
  Button,
  Modal,
  Stack,
  Flex,
  useTheme,
  spacing,
} from '@fti/design-system';

interface NodeDetailPanelProps {
  projectId: string;
  node: GraphNode | null;
  onUpdated: () => void;
  onClose: () => void;
  onEditingChange?: (editing: boolean) => void;
}

export const NodeDetailPanel: React.FC<NodeDetailPanelProps> = ({ projectId, node, onUpdated, onClose, onEditingChange }) => {
  const { theme } = useTheme();
  const { canUpdate, canDelete } = usePermissions();
  const { fetchData } = useAuthenticatedFetch();

  const [editing, setEditing] = useState(false);
  const [editProps, setEditProps] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const originalPropsRef = useRef<Record<string, unknown>>({});

  useEffect(() => {
    if (node) {
      originalPropsRef.current = { ...node.properties };
      setEditProps({ ...node.properties });
      setEditing(false);
    }
  }, [node]);

  if (!node) return null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Build the diff: deleted/renamed keys set to null, new/changed keys set to value
      const payload: Record<string, unknown> = {};
      const original = originalPropsRef.current;

      // Keys that were in original but not in editProps → set to null (deleted or renamed away)
      for (const key of Object.keys(original)) {
        if (!(key in editProps)) {
          payload[key] = null;
        }
      }

      // All keys in editProps → set their values
      for (const [key, val] of Object.entries(editProps)) {
        payload[key] = val;
      }

      await fetchData(`/dastarkhan/projects/${projectId}/graph/nodes/${encodeURIComponent(node.element_id)}`, {
        method: 'PATCH',
        body: JSON.stringify({ properties: payload }),
      });
      setEditing(false);
      onEditingChange?.(false);
      onUpdated();
    } catch {
      setError('Failed to update node');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await fetchData(`/dastarkhan/projects/${projectId}/graph/nodes/${encodeURIComponent(node.element_id)}`, {
        method: 'DELETE',
      });
      setShowDeleteConfirm(false);
      onClose();
      onUpdated();
    } catch {
      setError('Failed to delete node');
    }
  };

  const handleCancel = () => {
    setEditProps({ ...originalPropsRef.current });
    setEditing(false);
    onEditingChange?.(false);
  };

  return (
    <Card>
      <Card.Body>
        <Stack gap="sm">
          <Flex justify="between" align="start">
            <div>
              <h4 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: theme.colors.text.primary }}>
                {(node.properties.name as string) || (node.properties.Name as string) || node.labels[0] || 'Node'}
              </h4>
              <Flex gap="xs" style={{ marginTop: '4px' }}>
                {node.labels.map((l) => (
                  <span key={l} style={{
                    padding: `2px ${spacing.xs}`,
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    background: theme.colors.semantic.info.light,
                    color: theme.colors.semantic.info.default,
                  }}>{l}</span>
                ))}
              </Flex>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </Flex>

          <div>
            <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary, fontWeight: 500 }}>Properties</span>
            <div style={{ marginTop: '4px' }}>
              <PropertyEditor
                properties={editing ? editProps : node.properties}
                onChange={setEditProps}
                readOnly={!editing}
                initialKey={node.element_id}
              />
            </div>
          </div>

          {error && <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.8rem' }}>{error}</div>}

          <div style={{ fontSize: '0.7rem', color: theme.colors.text.secondary, fontFamily: 'monospace', borderTop: `1px solid ${theme.colors.border.default}`, paddingTop: spacing.xs }}>
            ID: {node.element_id}
          </div>

          <Flex gap="xs">
            {canUpdate(projectId) && !editing && (
              <Button variant="outline" size="sm" onClick={() => { setEditing(true); onEditingChange?.(true); }}>Edit</Button>
            )}
            {editing && (
              <>
                <Button variant="solid" color="primary" size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" size="sm" onClick={handleCancel}>Cancel</Button>
              </>
            )}
            {canDelete(projectId) && !editing && (
              <Button variant="outline" color="error" size="sm" onClick={() => setShowDeleteConfirm(true)}>Delete</Button>
            )}
          </Flex>
        </Stack>
      </Card.Body>

      {showDeleteConfirm && (
        <Modal isOpen onClose={() => setShowDeleteConfirm(false)}>
          <Modal.Header onClose={() => setShowDeleteConfirm(false)}>Confirm Delete</Modal.Header>
          <Modal.Body>
            <p>Delete this node and all its relationships? This cannot be undone.</p>
          </Modal.Body>
          <Modal.Footer>
            <Flex justify="end" gap="sm">
              <Button variant="outline" size="sm" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
              <Button variant="solid" color="error" size="sm" onClick={handleDelete}>Delete</Button>
            </Flex>
          </Modal.Footer>
        </Modal>
      )}
    </Card>
  );
};
